import { BlobServiceClient } from "@azure/storage-blob";

export type uploadBlobsParametersType = {
  containerName: string;
  files: { blobName: string; data: Blob | ArrayBuffer; mimeType: string }[];
};

class AzureBlobStorageBackend {
  private blobServiceClient: BlobServiceClient;

  constructor(accountName: string, accountKey: string) {
    this.blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net?${process.env.REACT_APP_AZURE_BLOB_ACCOUNT_SASS_TOKEN}`
    );
  }

  /**
   * Uploads a blob to the specified container
   */
  uploadBlob = async (
    containerName: string,
    blobName: string,
    data: Blob | ArrayBuffer,
    mimeType: string
  ) => {
    const containerClient =
      this.blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    await blockBlobClient.uploadData(data, {
      blobHTTPHeaders: {
        blobContentType: mimeType,
      },
    });
    return blockBlobClient.url.split("?")[0];
  };

  // Uploads a multiple blob to the specified container
  uploadBlobs = async ({ containerName, files }: uploadBlobsParametersType) => {
    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);

      const uploadFilesPromises = files.map(async (file) => {
        const blockBlobClient = containerClient.getBlockBlobClient(
          file.blobName
        );
        await blockBlobClient.uploadData(file.data, {
          blobHTTPHeaders: {
            blobContentType: file.mimeType,
          },
        });
        return blockBlobClient.url.split("?")[0];
      });
      const resolvedUploadFilesPromises = await Promise.all(
        uploadFilesPromises
      );
      return resolvedUploadFilesPromises;
    } catch (error) {
      console.log("error", error);
    }
  };
  /**
   * Uploads an Excel file to the specified container
   */
  uploadExcelFile = async (
    containerName: string,
    blobName: string,
    file: Blob
  ) => {
    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Set the content type to Excel
        },
      });
      return blockBlobClient.url.split("?")[0];
    } catch (error) {
      console.log("error", error);
    }
  };

  /**
   * Downloads a blob from the specified container
   */
  downloadBlob = async (containerName: string, blobName: string) => {
    const containerClient =
      this.blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    const downloadResponse = await blockBlobClient.download();
    const blobData = await blobToString(
      (await downloadResponse.blobBody) as Blob
    );
    return blobData;
  };

  /**
   * Deletes a blob from the specified container
   */
  deleteBlob = async (containerName: string, blobName: string) => {
    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      await blockBlobClient.delete();
      return true;
    } catch (error) {
      return true;
    }
  };
}

let _azureBlobStorageBackend: AzureBlobStorageBackend | null = null;

/**
 * Initializes the Azure Blob Storage backend
 * @param {*} accountName
 * @param {*} accountKey
 */
const initAzureBlobStorageBackend = (
  accountName: string,
  accountKey: string
) => {
  if (!_azureBlobStorageBackend) {
    _azureBlobStorageBackend = new AzureBlobStorageBackend(
      accountName,
      accountKey
    );
  }
  return _azureBlobStorageBackend;
};

/**
 * Returns the Azure Blob Storage backend instance
 */
const getAzureBlobStorageBackend = () => {
  return _azureBlobStorageBackend;
};

async function blobToString(blob: Blob): Promise<string> {
  const reader = new FileReader();
  return new Promise<string>((resolve, reject) => {
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}

export { initAzureBlobStorageBackend, getAzureBlobStorageBackend };
